import type { FormField } from '@lucid/lego';
import type { FieldValues } from 'react-hook-form';

/**
 * Default values for the Send Docusign form
 */
type SendDocusignFormDefaultValues = {
  prospectType?: string;
  companyName?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
};

/**
 * Callbacks for the Send Docusign form buttons
 */
type SendDocusignFormCallbacks = {
  onCancel?: () => void;
};

/**
 * Labels type for i18n support
 */
type Labels = Record<string, string> | undefined;

/**
 * Form fields configuration for the Send Docusign form
 * @param defaultValues - Optional default values for form fields
 * @param callbacks - Optional callbacks for form buttons
 * @param labels - Optional i18n labels
 * @returns Array of form field configurations
 */
export const createSendDocusignFormFields = (
  defaultValues?: SendDocusignFormDefaultValues,
  callbacks?: SendDocusignFormCallbacks,
  labels?: Labels
): FormField[] => {
  const isBusinessProspect = defaultValues?.prospectType === 'Business';
  const isPrivateProspect = defaultValues?.prospectType === 'Private';

  return [
    {
      id: 'businessName',
      fieldType: 'text' as const,
      name: 'businessName',
      apiKey: 'businessName',
      label: labels?.['checkout.docusign.businessName.label'] ?? 'BUSINESS NAME',
      placeholder: labels?.['checkout.docusign.businessName.placeholder'] ?? 'Enter business name',
      required: true,
      width: 'full' as const,
      defaultValue: defaultValues?.companyName,
      visible: isBusinessProspect,
      validationMessages: {
        required:
          labels?.['checkout.docusign.businessName.validation.required'] ??
          'Business name is required',
      },
      disabled: true,
    },
    {
      id: 'firstName',
      fieldType: 'text' as const,
      name: 'firstName',
      apiKey: 'firstName',
      label: labels?.['checkout.docusign.firstName.label'] ?? 'FIRST NAME',
      placeholder: labels?.['checkout.docusign.firstName.placeholder'] ?? 'Enter first name',
      required: true,
      width: 'half' as const,
      defaultValue: defaultValues?.firstName,
      visible: isPrivateProspect,
      validationMessages: {
        required:
          labels?.['checkout.docusign.firstName.validation.required'] ?? 'First name is required',
      },
      disabled: true,
    },
    {
      id: 'lastName',
      fieldType: 'text' as const,
      name: 'lastName',
      apiKey: 'lastName',
      label: labels?.['checkout.docusign.lastName.label'] ?? 'LAST NAME',
      placeholder: labels?.['checkout.docusign.lastName.placeholder'] ?? 'Enter last name',
      required: true,
      width: 'half' as const,
      defaultValue: defaultValues?.lastName,
      visible: isPrivateProspect,
      validationMessages: {
        required:
          labels?.['checkout.docusign.lastName.validation.required'] ?? 'Last name is required',
      },
      disabled: true,
    },
    {
      id: 'email',
      fieldType: 'email',
      name: 'email',
      apiKey: 'email',
      label: labels?.['checkout.docusign.email.label'] ?? 'EMAIL',
      placeholder: labels?.['checkout.docusign.email.placeholder'] ?? 'Enter email address',
      required: true,
      width: 'full',
      defaultValue: defaultValues?.email,
      validationMessages: {
        required: labels?.['checkout.docusign.email.validation.required'] ?? 'Email is required',
        pattern:
          labels?.['checkout.docusign.email.validation.pattern'] ??
          'Please enter a valid email address',
      },
      disabled: true,
    },
    {
      id: 'formButtons',
      fieldType: 'buttonGroup',
      align: 'left',
      buttons: [
        {
          id: 'cancelButton',
          label: labels?.['checkout.docusign.cancel'] ?? 'CANCEL',
          type: 'secondary',
          size: 'medium',
          isFluid: true,
          color: 'neutral',
          onClick: (_data: FieldValues) => {
            callbacks?.onCancel?.();
          },
        },
        {
          id: 'submitButton',
          label: labels?.['checkout.docusign.send'] ?? 'SEND',
          type: 'primary',
          size: 'medium',
          isFluid: true,
          color: 'neutral',
        },
      ],
    },
  ];
};
